<template>
  <div ref="locationPopup"
    class="cw-flex cw-flex-col cw-justify-center cw-w-[300px]"
    role="status">
    <template v-if="location">
      <div class="cw-flex cw-justify-between">
        <div>
          <h3 class="cw-text-lg cw-font-medium">
            {{ location.name }}
          </h3>
          <span v-if="badgeText"
            class="cw-w-fit cw-block cw-my-1 cw-p-1 cw-font-semibold cw-text-xs cw-rounded cw-bg-blue-200 cw-text-blue-700">{{ badgeText }}</span>
        </div>
        <img v-if="showImage" 
          :src="`https://media.megaport.com/datacentre/${location.dc.id}/62x30.png`"
          class="cw-text-right cw-max-w-16 cw-object-scale-down"
          :alt="location.name"
          @error="showImage = false">
      </div>
      <address class="cw-mb-2 cw-leading-4">
        {{ location.address.street || '' }} {{ location.address.suburb || '' }}
        <br>
        {{ location.address.city || '' }} {{ location.address.state || '' }} {{ location.address.postcode || '' }}
        <br>
        {{ location.address.country || '' }}
      </address>
      <a :href="contactHref"
        class="cw-block cw-w-fit cw-text-blue-600">{{ $t('enabled-locations-map.enquire-about-this-location') }}</a>
    </template>
  </div>
</template>

<script>
import Mapbox from 'mapbox-gl'
import { updateSearchParams } from '@/shared/helpers.js'

export default {
  name: 'MapPopup',

  props: {
    mapContext: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    deepLinkLocation: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },

  data() {
    return {
      mapboxPopup: new Mapbox.Popup({ anchor: 'left', offset: { bottom: [0, -25], left: [15, 0] } }),
      location: null,
      showImage: true,
    }
  },

  computed: {
    badgeText() {
      switch (this.location?.status) {
        case 'Deployment':
          return this.$t('enabled-locations-map.coming-soon')
        case 'Extended':
          return this.$t('enabled-locations-map.extended-location')
        default:
          return null
      }
    },
    contactHref() {
      if (!this.location) return ''
      const locParam = this.location.status === 'Extended' ? `extended&type=extended` : encodeURIComponent(this.location.siteCode)
      return `https://megaport.com/contact/sales?loc=${locParam}&dcName=${encodeURIComponent(this.location.name)}`
    },
  },

  created() {
    window.addEventListener('mp_showLocationPopup', this.showLocationPopup)
  },

  mounted() {
    this.mapboxPopup.setDOMContent(this.$refs.locationPopup)
    if (Object.keys(this.deepLinkLocation).length > 0) {
      this.showLocationPopup(null, this.deepLinkLocation)
    }
  },

  destroyed() {
    window.removeEventListener('mp_showLocationPopup', this.showLocationPopup)
  },

  methods: {
    async showLocationPopup(event, locationObj) {
      const location = locationObj || event.detail.location
      if (typeof location.address === 'string') {
        location.address = JSON.parse(location.address)
        location.dc = JSON.parse(location.dc)
      }
      updateSearchParams({ locationId: location.id })

      this.location = location
      this.showImage = true
      await this.$nextTick() // wait a sec for the vDom to mutate before grabbing innerHTML

      this.mapboxPopup
        .setLngLat([location.longitude, location.latitude])
        .addTo(this.mapContext)
        .once('close', async () => {
          // Need to wait a tick to make sure we aren't just changing location
          await this.$nextTick()
          // Only clear the search params when we're deselecting a location
          if (this.location?.id === location.id) updateSearchParams()
        })
    },
  },
}
</script>

<style>
.mapboxgl-popup-close-button {
  right: 0.5rem;
  font-size: 20px;
}

.mapboxgl-popup-content {
  box-shadow: 4px 4px 8px 2px rgba(0, 0, 0, 0.3);
}
</style>

